import React from 'react';
import BaseScadaNode from './BaseScadaNode';

const PHASE_LABELS = ['IDLE', 'RINSE', 'CAUST', 'RINSE', 'ACID', 'RINSE', 'DRAIN'];
const PHASE_COLORS = ['#64748B', '#3B82F6', '#F97316', '#3B82F6', '#A855F7', '#3B82F6', '#22C55E'];

export function CipSkidSVG({ config = {}, width = 160, height = 100, liveValues = {} }) {
    const running = parseFloat(liveValues.state ?? 0) >= parseFloat(config.threshold ?? 0.5);
    const hasError = liveValues.error === 1 || liveValues.error === true ||
        liveValues.error === '1' || liveValues.error === 'true';
    const statusColor = hasError ? '#EF4444' : running ? '#22C55E' : '#64748B';

    const phase = parseInt(liveValues.cip_phase ?? 0);
    const phaseLabel = PHASE_LABELS[phase] || 'IDLE';
    const phaseColor = PHASE_COLORS[phase] || '#64748B';

    const lvWater = parseFloat(liveValues.level_water ?? 80);
    const lvCaustic = parseFloat(liveValues.level_caustic ?? 80);
    const lvAcid = parseFloat(liveValues.level_acid ?? 80);

    // Layout: 3 tanks side by side, pump below, phase indicator at top
    const pad = 6;
    const tankW = Math.floor((width - pad * 5) / 3);
    const tankH = Math.floor(height * 0.52);
    const tankY = 20;

    const pumpCX = width / 2;
    const pumpCY = tankY + tankH + 16;
    const pumpR = 9;

    const drawTank = (x, y, w, h, level, color, label) => {
        const fillH = h * Math.min(1, Math.max(0, level / 100));
        const fillY = y + h - fillH;
        const clipId = `cip_${label}_${x}`;
        return (
            <g key={label}>
                <rect x={x} y={y} width={w} height={h} rx={3}
                    fill="#0F172A" stroke="#475569" strokeWidth={1.5} />
                <clipPath id={clipId}>
                    <rect x={x} y={y} width={w} height={h} rx={3} />
                </clipPath>
                <rect x={x} y={fillY} width={w} height={fillH}
                    fill={color} opacity={0.7} clipPath={`url(#${clipId})`} />
                <text x={x + w / 2} y={y + h + 10} textAnchor="middle"
                    fill="#94A3B8" fontSize={7} fontWeight="bold">
                    {label}
                </text>
            </g>
        );
    };

    const t1x = pad;
    const t2x = pad * 2 + tankW;
    const t3x = pad * 3 + tankW * 2;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {/* Phase indicator bar at top */}
            <rect x={pad} y={2} width={width - pad * 2} height={14} rx={3}
                fill={phaseColor} opacity={running ? 0.85 : 0.3} />
            <text x={width / 2} y={13} textAnchor="middle"
                fill="#F1F5F9" fontSize={9} fontWeight="bold">
                {phaseLabel}
            </text>

            {/* Three tanks */}
            {drawTank(t1x, tankY, tankW, tankH, lvWater, '#3B82F6', 'H2O')}
            {drawTank(t2x, tankY, tankW, tankH, lvCaustic, '#F97316', 'NaOH')}
            {drawTank(t3x, tankY, tankW, tankH, lvAcid, '#A855F7', 'ACID')}

            {/* Piping lines from tanks to pump */}
            <line x1={t1x + tankW / 2} y1={tankY + tankH} x2={pumpCX} y2={pumpCY - pumpR}
                stroke="#475569" strokeWidth={1} />
            <line x1={t2x + tankW / 2} y1={tankY + tankH} x2={pumpCX} y2={pumpCY - pumpR}
                stroke="#475569" strokeWidth={1} />
            <line x1={t3x + tankW / 2} y1={tankY + tankH} x2={pumpCX} y2={pumpCY - pumpR}
                stroke="#475569" strokeWidth={1} />

            {/* CIP Pump (circle with arrow) */}
            <circle cx={pumpCX} cy={pumpCY} r={pumpR}
                fill="#1E293B" stroke={statusColor} strokeWidth={2} />
            {/* Impeller triangle */}
            <polygon
                points={`${pumpCX - 4},${pumpCY + 4} ${pumpCX + 4},${pumpCY + 4} ${pumpCX},${pumpCY - 5}`}
                fill={statusColor} opacity={0.8}
            />
            {running && (
                <circle cx={pumpCX} cy={pumpCY} r={pumpR}
                    fill="none" stroke={statusColor} strokeWidth={1}>
                    <animate attributeName="r" values={`${pumpR};${pumpR + 3};${pumpR}`}
                        dur="1s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.6;0;0.6"
                        dur="1s" repeatCount="indefinite" />
                </circle>
            )}

            {/* Supply outlet stub (right side) */}
            <rect x={width - 4} y={pumpCY - 3} width={4} height={6} rx={1} fill="#475569" />
            <line x1={pumpCX + pumpR} y1={pumpCY} x2={width - 4} y2={pumpCY}
                stroke="#475569" strokeWidth={1.5} />

            {/* Return inlet stub (left side) */}
            <rect x={0} y={pumpCY - 3} width={4} height={6} rx={1} fill="#475569" />
            <line x1={4} y1={pumpCY} x2={pumpCX - pumpR} y2={pumpCY}
                stroke="#475569" strokeWidth={1.5} />

            {/* Status dot */}
            <circle cx={width - 10} cy={8} r={4}
                fill={statusColor} stroke="#0F172A" strokeWidth={1} />

            {/* Error flash */}
            {hasError && (
                <rect x={1} y={1} width={width - 2} height={height - 2} rx={4}
                    fill="none" stroke="#EF4444" strokeWidth={2}>
                    <animate attributeName="stroke-opacity" values="1;0;1" dur="0.6s" repeatCount="indefinite" />
                </rect>
            )}
        </svg>
    );
}

export default function CipSkidNode(props) {
    const { data } = props;
    const w = data?.width || 160;
    const h = data?.height || 100;
    return (
        <BaseScadaNode {...props}>
            <CipSkidSVG config={data?.config} width={w} height={h} liveValues={data?.liveValues || {}} />
            {data?.label && (
                <div style={{ textAlign: 'center', fontSize: 10, color: '#94A3B8', marginTop: 2,
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {data.label}
                </div>
            )}
        </BaseScadaNode>
    );
}
